import React from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { QuickLeadForm } from "@/components/forms/QuickLeadForm";
import { CtaBlock } from "@/components/CtaBlock";
import NotFound from "@/pages/public/NotFound";
import { Clock, User, ShieldCheck, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { data: g, isLoading, isError } = useQuery({
    queryKey: ["guide", slug],
    queryFn: () => api.get(`/guides/${slug}`).then((r) => r.data),
    retry: false,
  });

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-20 text-slate-500">Caricamento...</div>;
  if (isError || !g) return <NotFound />;

  const fmt = (d) => d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article", headline: g.title, description: g.excerpt,
    author: { "@type": "Person", name: g.author?.name }, datePublished: g.published_at, dateModified: g.updated_at,
    publisher: { "@type": "Organization", name: "SENIORCAR" },
  };

  return (
    <>
      <Seo title={g.meta_title || g.title} description={g.meta_description || g.excerpt} keywords={g.primary_keyword} jsonLd={jsonLd} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <Breadcrumbs items={[{ label: "Guide", path: "/guide" }, { label: g.category }, { label: g.title }]} />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">{g.category}</span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>{g.title}</h1>

        <div className="mt-5 flex items-center flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500 border-y border-slate-200 py-4">
          <span className="flex items-center gap-1.5"><User size={16} className="text-emerald-700" /> Scritto da{" "}
            {g.author?.slug
              ? <Link to={`/autori/${g.author.slug}`} className="font-semibold text-emerald-800 hover:underline" data-testid="article-author-link">{g.author?.name}</Link>
              : <strong className="text-slate-700">{g.author?.name}</strong>}
          </span>
          {g.reviewer && <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-700" /> Revisione:{" "}
            {g.reviewer?.slug
              ? <Link to={`/autori/${g.reviewer.slug}`} className="font-semibold text-emerald-800 hover:underline" data-testid="article-reviewer-link">{g.reviewer?.name}</Link>
              : <strong className="text-slate-700">{g.reviewer?.name}</strong>}
          </span>}
          <span className="flex items-center gap-1.5"><Clock size={16} className="text-emerald-700" /> {g.reading_time} min</span>
        </div>
        <p className="text-sm text-slate-500 mt-3">Ultimo aggiornamento: {fmt(g.updated_at)}</p>

        <div className="sc-prose mt-6" dangerouslySetInnerHTML={{ __html: g.body }} />

        <div className="mt-10"><QuickLeadForm title="Vuoi un consiglio personalizzato? Ti richiamiamo noi." compact /></div>
      </article>

      {g.related?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Guide correlate</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {g.related.map((r) => (
              <Link key={r.slug} to={`/guide/${r.slug}`} className="rounded-xl bg-white border border-slate-200 p-4 hover:shadow-md transition-all" data-testid={`related-${r.slug}`}>
                <h3 className="text-sm font-bold text-slate-900 leading-snug">{r.title}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-emerald-800 font-semibold text-xs">Leggi <ArrowRight size={12} /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 my-14"><CtaBlock variant="test" /></div>
    </>
  );
}
